if (!customElements.get('shoppable-video-carousel')) {
  customElements.define('shoppable-video-carousel', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.sourceTasks?.forEach(task => task.abort());
      this.cardObserver?.disconnect();
      this.sectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.shopping = false;
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.rail = this.querySelector('[data-rail]');
      this.railWidth = this.rail.clientWidth;
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.videos = [...this.querySelectorAll('video[data-source]')];
      this.modals = [...this.querySelectorAll('quick-buy-modal')];
      this.visible = new Set();
      this.manual = new Map();
      this.sourceTasks = new Map();
      this.playlists = new Map();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.allPlaying = this.dataset.autoplay === 'true' && !this.motion.matches && !navigator.connection?.saveData;
      this.pageReady = document.readyState === 'complete';
      this.onScreen = false;
      const startAt = this.dataset.start === 'first' ? 1 : parseInt(this.dataset.start, 10);
      this.active = startAt > 0 ? Math.min(startAt, this.cards.length) - 1 : Math.max(0, Math.floor((this.cards.length - 1) / 2));
      this.videos.forEach(video => {
        video.controls = false;
        const card = video.closest('[data-card]');
        card.querySelectorAll('[data-play], [data-mute], [data-select]').forEach(button => button.hidden = false);
        video.addEventListener('playing', () => {
          video.classList.add('is-playing', 'has-frame');
          this.updatePlayButton(video);
        }, {signal});
        video.addEventListener('pause', () => {
          video.classList.remove('is-playing');
          this.updatePlayButton(video);
        }, {signal});
        video.addEventListener('error', () => {
          if (video.dataset.delivery === 'hls' && video.hasAttribute('src')) {
            video.dataset.delivery = 'mp4';
            video.src = this.mp4Source(video).url;
            this.updatePlayback();
            return;
          }
          card.querySelector('.svc__error').hidden = false;
          card.querySelectorAll('[data-play], [data-mute], [data-select]').forEach(button => button.hidden = true);
        }, {signal});
      });
      this.cardObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => entry.isIntersecting && entry.intersectionRatio >= .25 ? this.visible.add(entry.target) : this.visible.delete(entry.target));
        this.updatePlayback();
      }, {root: this.rail, threshold: [0, .25]});
      this.cards.forEach(card => this.cardObserver.observe(card));
      this.sectionObserver = new IntersectionObserver(entries => {
        this.onScreen = entries.some(entry => entry.isIntersecting);
        this.updatePlayback();
      });
      this.sectionObserver.observe(this);
      this.querySelectorAll('[data-step]').forEach(button => button.hidden = this.cards.length < 2);
      this.addEventListener('pointerdown', event => {
        const button = event.target.closest('[data-select]');
        this.tapSelection = button ? {button, selected: button.closest('[data-card]') === this.cards[this.active]} : null;
      }, {signal});
      this.addEventListener('pointercancel', () => this.tapSelection = null, {signal});
      this.addEventListener('click', event => {
        const card = event.target.closest('[data-card]');
        const video = card?.querySelector('video');
        if (event.target.closest('[data-step]')) this.move(Number(event.target.closest('[data-step]').dataset.step));
        if (event.target.closest('[data-play]') && video) {
          if (this.cards.indexOf(card) !== this.active) this.scrollToCard(this.cards.indexOf(card));
          this.manual.set(video, video.paused);
          this.updatePlayback();
        }
        if (event.target.closest('[data-mute]') && video) {
          this.scrollToCard(this.cards.indexOf(card));
          this.toggleSound(video);
        }
        if (event.target.closest('[data-select]') && video) {
          const index = this.cards.indexOf(card);
          const wasSelected = event.detail && this.tapSelection?.button === event.target.closest('[data-select]') ? this.tapSelection.selected : index === this.active;
          this.tapSelection = null;
          if (!wasSelected || !video.hasAttribute('src')) {
            this.setMuted(video, true);
            this.manual.set(video, true);
            this.scrollToCard(index);
          } else this.toggleSound(video);
        }
        if (event.target.closest('.svc__add[aria-controls]')) {
          this.videos.forEach(player => player.pause());
          this.shopping = true;
        }
      }, {signal});
      this.modals.forEach(modal => modal.addEventListener('dialog:after-hide', () => {this.shopping = false; this.updatePlayback();}, {signal}));
      document.addEventListener('cart:change', () => {this.shopping = false; this.updatePlayback();}, {signal});
      this.rail.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          if (this.programmaticScroll || this.rail.clientWidth !== this.railWidth) return;
          const rect = this.rail.getBoundingClientRect(), centre = rect.left + rect.width / 2;
          this.active = this.cards.reduce((best, card, index) => {
            const distance = item => {const box = item.getBoundingClientRect(); return Math.abs(box.left + box.width / 2 - centre);};
            return distance(card) < distance(this.cards[best]) ? index : best;
          }, 0);
          this.updateActive();
        });
      }, {passive: true, signal});
      this.rail.addEventListener('keydown', event => {
        if (event.target !== this.rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const rtl = getComputedStyle(this.rail).direction === 'rtl';
        if (event.key === 'Home') this.scrollToCard(0);
        else if (event.key === 'End') this.scrollToCard(this.cards.length - 1);
        else this.move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
      }, {signal});
      document.addEventListener('visibilitychange', () => this.updatePlayback(), {signal});
      window.addEventListener('load', () => {this.pageReady = true; this.updatePlayback();}, {signal, once: true});
      this.motion.addEventListener('change', () => {
        this.allPlaying = this.dataset.autoplay === 'true' && !this.motion.matches && !navigator.connection?.saveData;
        this.manual.clear();
        this.updatePlayback();
      }, {signal});
      document.addEventListener('shopify:block:select', event => {
        const index = this.cards.findIndex(card => card.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.scrollToCard(index, 'instant');
      }, {signal});
      this.resizeObserver = new ResizeObserver(() => {
        if (this.rail.clientWidth === this.railWidth) return;
        this.railWidth = this.rail.clientWidth;
        this.scrollToCard(this.active, 'instant');
      });
      this.resizeObserver.observe(this.rail);
      this.updateActive();
      this.initialFrame = requestAnimationFrame(() => this.scrollToCard(this.active, 'instant'));
    }
    updatePlayButton(video) {
      video.closest('[data-card]').querySelector('[data-play]').setAttribute('aria-pressed', String(!video.paused));
    }
    setMuted(video, muted) {
      video.defaultMuted = muted;
      video.muted = muted;
      const button = video.closest('[data-card]').querySelector('[data-mute]');
      button.setAttribute('aria-label', muted ? button.dataset.unmuteLabel : button.dataset.muteLabel);
      button.querySelector('[data-muted-icon]').hidden = !muted;
      button.querySelector('[data-sound-icon]').hidden = muted;
      const select = video.closest('[data-card]').querySelector('[data-select]');
      const selected = video.closest('[data-card]') === this.cards[this.active];
      select.setAttribute('aria-label', `${selected ? button.getAttribute('aria-label') : select.dataset.playLabel}: ${select.dataset.videoLabel}`);
    }
    toggleSound(video) {
      const unmute = video.muted;
      this.videos.forEach(other => {if (other !== video) this.setMuted(other, true);});
      this.setMuted(video, !unmute);
      clearTimeout(this.playbackTimer);
      this.settling = false;
      if (unmute) {
        this.manual.set(video, true);
        video.volume = 1;
        if (!video.hasAttribute('src')) {
          this.cancelSource(video);
          video.dataset.delivery = 'mp4';
          video.src = this.mp4Source(video).url;
        }
        video.play().catch(error => {
          if (error.name === 'NotAllowedError') this.setMuted(video, true);
          this.updatePlayButton(video);
        });
      }
      this.updatePlayback();
    }
    sourceLimit() {
      const connection = navigator.connection;
      return connection?.saveData || /(^|-)2g$|3g/.test(connection?.effectiveType || '') || (connection?.downlink > 0 && connection.downlink < 1.5) ? 480 : 720;
    }
    fittingSource(video, sources) {
      const sorted = sources.filter(source => source.url).sort((a, b) => (a.height || Infinity) - (b.height || Infinity) || (a.bandwidth || 0) - (b.bandwidth || 0));
      const limited = sorted.filter(source => source.height && source.height <= this.sourceLimit());
      const choices = limited.length ? limited : sorted;
      // Size for the playing (full 9:16) card, so a neighbour preloaded at its smaller size is still sharp once it plays
      const height = Math.min(Math.round(video.clientWidth * 16 / 9), this.sourceLimit());
      return choices.find(source => source.width >= video.clientWidth && source.height >= height) || choices[choices.length - 1];
    }
    mp4Source(video) {
      return this.fittingSource(video, [...video.querySelectorAll('source[type="video/mp4"]')].map(source => ({url: source.dataset.src, width: Number(source.dataset.width), height: Number(source.dataset.height)}))) || {url: video.dataset.source};
    }
    async streamingSource(video, signal) {
      const fallback = {...this.mp4Source(video), delivery: 'mp4'};
      const hls = [...video.querySelectorAll('source')].find(source => /mpegurl/i.test(source.type) && video.canPlayType(source.type));
      if (!hls) return fallback;
      const url = new URL(hls.dataset.src, location.href).href;
      let variants = this.playlists.get(url);
      if (!variants) {
        const response = await fetch(url, {signal});
        if (!response.ok) return fallback;
        const lines = (await response.text()).split(/\r?\n/).map(line => line.trim());
        variants = [];
        let stream;
        for (const line of lines) {
          if (line.startsWith('#EXT-X-STREAM-INF:')) {
            const resolution = line.match(/RESOLUTION=(\d+)x(\d+)/), bandwidth = line.match(/(?:[:,])BANDWIDTH=(\d+)/);
            stream = resolution ? {width: Number(resolution[1]), height: Number(resolution[2]), bandwidth: Number(bandwidth?.[1] || 0)} : null;
          } else if (stream && line && !line.startsWith('#')) {
            variants.push({...stream, url: new URL(line, url).href});
            stream = null;
          }
        }
        if (!variants.length) return {...fallback, url, delivery: 'hls'};
        this.playlists.set(url, variants);
      }
      return {...this.fittingSource(video, variants), delivery: 'hls'};
    }
    cancelSource(video) {
      this.sourceTasks.get(video)?.abort();
      this.sourceTasks.delete(video);
    }
    prepareSource(video) {
      if (this.sourceTasks.has(video)) return;
      const task = new AbortController();
      this.sourceTasks.set(video, task);
      this.streamingSource(video, task.signal).catch(error => error.name === 'AbortError' ? null : {...this.mp4Source(video), delivery: 'mp4'}).then(source => {
        if (!source || this.sourceTasks.get(video) !== task || !this.isConnected) return;
        this.sourceTasks.delete(video);
        video.dataset.delivery = source.delivery;
        video.preload = 'auto';
        video.src = source.url;
        this.updatePlayback();
      });
    }
    releaseVideo(video) {
      this.cancelSource(video);
      video.pause();
      this.setMuted(video, true);
      if (video.hasAttribute('src')) {
        video.removeAttribute('src');
        video.load();
        video.classList.remove('has-frame', 'is-playing');
      }
    }
    canPreload() {
      return this.pageReady && this.onScreen && !document.hidden && !this.shopping && this.sourceLimit() > 480;
    }
    updatePlayback() {
      const selected = this.cards[this.active]?.querySelector('video');
      // Keep the videos either side of the playing one loaded (paused, muted) so swiping to them starts instantly
      const neighbours = this.canPreload() ? [this.active - 1, this.active + 1].map(index => this.cards[index]?.querySelector('video')).filter(Boolean) : [];
      this.videos.forEach(video => {
        if (neighbours.includes(video)) {
          video.pause();
          this.setMuted(video, true);
          if (!video.hasAttribute('src')) this.prepareSource(video);
        } else if (video !== selected || !this.onScreen || document.hidden) this.releaseVideo(video);
      });
      if (!selected) return;
      const card = selected.closest('[data-card]');
      const play = this.pageReady && this.onScreen && !document.hidden && !this.shopping && !this.settling && this.visible.has(card) && (this.manual.get(selected) ?? this.allPlaying);
      if (play) {
        if (!selected.hasAttribute('src')) {this.prepareSource(selected); return;}
        if (selected.paused && !selected.error) selected.play().catch(() => this.updatePlayButton(selected));
      } else selected.pause();
    }

    updateActive() {
      this.cards.forEach((card, index) => card.classList.toggle('is-active', index === this.active));
      this.querySelector('[data-step="-1"]').disabled = this.active <= 0;
      this.querySelector('[data-step="1"]').disabled = this.active >= this.cards.length - 1;
      this.videos.forEach(video => this.setMuted(video, video.closest('[data-card]') === this.cards[this.active] ? video.muted : true));
      clearTimeout(this.playbackTimer);
      this.settling = true;
      this.playbackTimer = setTimeout(() => {this.settling = false; this.updatePlayback();}, 180);
    }
    move(step) {this.scrollToCard(Math.max(0, Math.min(this.cards.length - 1, this.active + step)));}
    scrollToCard(index, behavior) {
      const card = this.cards[index];
      if (!card) return;
      this.active = index;
      this.updateActive();
      // Cards use scroll-snap-stop: always, which would stop a jump of several cards at the next card, so snapping
      // is paused while the carousel scrolls itself, and the scroll listener ignores that movement
      this.programmaticScroll = true;
      this.rail.style.scrollSnapType = 'none';
      clearTimeout(this.snapTimer);
      const finish = () => {
        clearTimeout(this.snapTimer);
        this.rail.removeEventListener('scrollend', finish);
        this.rail.style.scrollSnapType = '';
        this.programmaticScroll = false;
      };
      this.rail.addEventListener('scrollend', finish, {once: true});
      this.snapTimer = setTimeout(finish, 1000);
      const box = card.getBoundingClientRect(), rail = this.rail.getBoundingClientRect();
      this.rail.scrollBy({left: box.left + box.width / 2 - rail.left - rail.width / 2, behavior: behavior || (this.motion.matches ? 'instant' : 'smooth')});
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.sourceTasks?.forEach(task => task.abort());
      this.sourceTasks?.clear();
      this.cardObserver?.disconnect();
      this.sectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.initialFrame);
      clearTimeout(this.playbackTimer);
      clearTimeout(this.snapTimer);
      this.videos?.forEach(video => {video.pause(); video.removeAttribute('src'); video.load();});
      this.modals?.forEach(modal => {if (modal.hasAttribute('open')) modal.hide?.();});
    }
  });
}
